import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { APIHOST } from '../../../common';
import type { ConferenceGroup } from '../../../types/conferenceGroup';

interface HotelPickerTestData {
  conferenceGroupId: string;
  oneBedRoomsNeeded: number;
  twoBedRoomsNeeded: number;
  busParkingNeeded: boolean;
  transportationNeeded: boolean;
  checkinDate: Date | null;
  checkoutDate: Date | null;
}

export default function TestHotelPicker() {
  const [conferenceGroups, setConferenceGroups] = useState<ConferenceGroup[]>([]);
  const [selectedConferenceGroup, setSelectedConferenceGroup] = useState<ConferenceGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<HotelPickerTestData>({
    conferenceGroupId: '',
    oneBedRoomsNeeded: 0,
    twoBedRoomsNeeded: 0,
    busParkingNeeded: false,
    transportationNeeded: false,
    checkinDate: null,
    checkoutDate: null
  });

  // Fetch conference groups on component mount
  useEffect(() => {
    const fetchConferenceGroups = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${APIHOST}/api/conference-groups?populate=conference`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch conference groups: ${response.statusText}`);
        }

        const data = await response.json();
        setConferenceGroups(data.data || []);
      } catch (error) {
        console.error('Error fetching conference groups:', error);
        setError(`Failed to load conference groups: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    fetchConferenceGroups();
  }, []);

  const handleInputChange = (field: keyof HotelPickerTestData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Handle conference group selection to show A-tier room info
    if (field === 'conferenceGroupId') {
      const selectedGroup = conferenceGroups.find(group => group.documentId === value);
      setSelectedConferenceGroup(selectedGroup || null);
    }
  };

  const handleTestHotelPicker = async () => {
    // Validate required fields
    if (!formData.conferenceGroupId || !formData.checkinDate || !formData.checkoutDate) {
      setError('Please fill in all required fields (Conference Group, Check-in Date, Check-out Date)');
      return;
    }

    if (formData.oneBedRoomsNeeded <= 0 && formData.twoBedRoomsNeeded <= 0) {
      setError('Please specify at least one room (one bed or two bed)');
      return;
    }

    setLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // This is a placeholder for the actual hotel picker testing API
      // You'll need to implement the actual API endpoint that tests hotel availability
      const testPayload = {
        conferenceGroupId: formData.conferenceGroupId,
        oneBedRoomsNeeded: formData.oneBedRoomsNeeded,
        twoBedRoomsNeeded: formData.twoBedRoomsNeeded,
        busParkingNeeded: formData.busParkingNeeded,
        transportationNeeded: formData.transportationNeeded,
        checkinDate: formData.checkinDate?.toISOString(),
        checkoutDate: formData.checkoutDate?.toISOString()
      };

      // For now, we'll simulate the API call
      setTimeout(() => {
        setTestResults({
          status: 'success',
          message: 'Hotel picker test completed',
          data: testPayload,
          availableHotels: Math.floor(Math.random() * 10) + 1,
          timestamp: new Date().toISOString()
        });
        setLoading(false);
      }, 2000);

      // Uncomment and modify this when the actual API endpoint is ready:
      /*
      const response = await fetch(`${APIHOST}/api/test-hotel-picker`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testPayload)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const results = await response.json();
      setTestResults(results);
      */
      
    } catch (error) {
      console.error('Error testing hotel picker:', error);
      setError(`Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Hotel Picker Testing
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Test the hotel picker functionality with different parameters.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Conference Group</InputLabel>
                <Select
                  value={formData.conferenceGroupId}
                  label="Conference Group"
                  onChange={(e) => handleInputChange('conferenceGroupId', e.target.value)}
                >
                  {conferenceGroups.map((group) => (
                    <MenuItem key={group.documentId} value={group.documentId}>
                      {group.name} - {group.longName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {selectedConferenceGroup && (
              <Grid size={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    A-Tier Room Information for {selectedConferenceGroup.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>A-Tier Rooms Booked:</strong> {selectedConferenceGroup.aTierReserved} / {selectedConferenceGroup.aTierAllowed}
                  </Typography>
                  <Typography variant="body2">
                    <strong>A-Tier Rooms Remaining:</strong> {selectedConferenceGroup.aTierAllowed - selectedConferenceGroup.aTierReserved}
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="One Bed Rooms Needed"
                value={formData.oneBedRoomsNeeded}
                onChange={(e) => handleInputChange('oneBedRoomsNeeded', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Two Bed Rooms Needed"
                value={formData.twoBedRoomsNeeded}
                onChange={(e) => handleInputChange('twoBedRoomsNeeded', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.busParkingNeeded}
                    onChange={(e) => handleInputChange('busParkingNeeded', e.target.checked)}
                  />
                }
                label="Bus Parking Needed"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.transportationNeeded}
                    onChange={(e) => handleInputChange('transportationNeeded', e.target.checked)}
                  />
                }
                label="Transportation Needed"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="Check-in Date *"
                value={formData.checkinDate}
                onChange={(date) => handleInputChange('checkinDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="Check-out Date *"
                value={formData.checkoutDate}
                onChange={(date) => handleInputChange('checkoutDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>

            <Grid size={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleTestHotelPicker}
                  disabled={loading}
                  size="large"
                >
                  {loading ? <CircularProgress size={24} /> : 'Test Hotel Picker'}
                </Button>
                
                {loading && (
                  <Typography variant="body2" color="text.secondary">
                    Running hotel picker test...
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {testResults && (
            <Box sx={{ mt: 4 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Test completed successfully!
              </Alert>
              
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Test Results
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2">
                        <strong>Available Hotels:</strong> {testResults.availableHotels}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2">
                        <strong>Test Time:</strong> {new Date(testResults.timestamp).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Test Parameters:</strong>
                      </Typography>
                      <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '8px', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(testResults.data, null, 2)}
                      </pre>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
}